import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Membership } from "../api/memberships";
import { HOUSEHOLDS_SECTION } from "../sections";
import { CheckIcon, ChevronIcon } from "./icons";
import { Menu, MenuLabel } from "./Menu";

/**
 * **The family switcher — and what it switches is the URL** (ADR 0021 decision 2, issue #301).
 *
 * A browser has navigation, so the selection is a route: choosing a family changes
 * `/families/:familyId/…` and nothing else changes at all. There is no "current family" held
 * in this bundle or in `localStorage`, so a link a member sends themselves opens the family
 * they meant, and two tabs can show two families.
 *
 * **This is not the same setting as the assistant's, and #360 decided that rather than
 * inheriting it.** ADR 0023 shipped `USER#<userId>` / `ACTIVE_FAMILY` — which family a
 * caller's MCP calls resolve to — and left open whether the panel's switcher should write it
 * too. It does not. Two panel tabs can be open on two households at once, so there is no
 * single "current" here to write; and a link somebody opens is a *read*, which should not
 * silently retarget what their assistant writes to. `switch_family` is how that selection
 * moves, and it says so in the conversation where the consequence is.
 *
 * **Creating a household is the exception, and it is the ADR's own** (decision 4: *"the flow
 * that creates the ambiguity should resolve it"*). `commitFamilyCreation` seeds the selection
 * inside the create's transaction on every surface, so *Your households* → **Start it** does
 * point the person's assistant at what they just made. That is not this control, and it is
 * not the same act: a create is a deliberate decision that a household exists, where
 * switching is a read of one that already did.
 *
 * **Choosing is not authorization.** The server re-derives an `AuthContext` for the asserted
 * id on every request through `resolveAssertedFamily`, which re-reads the membership strongly
 * consistently, the role, and whether the family still exists. This control only decides what
 * to ask for — which is the sentence the wireframes put beside it too.
 *
 * **One family is a name, not a control**, and that survives #360 deliberately: a menu with a
 * single family in it implies a choice nobody has, and starting another lives on its own page
 * rather than behind a caret. It keeps the same bordered shape, minus the caret, so the header
 * does not rearrange itself when a second family appears.
 *
 * **What each row says beside the name, and what it still does not.** Since #360 every row —
 * and the one-family plate — carries the caller's **own** standing: *Owner* or *Member*. That
 * is ADR 0023 decision 8's distinguishing field, and it is the caller's own `MEMBER#` row, so
 * it discloses nothing about anybody else. The wireframes' second line — *"with Priya and
 * Dan"* — is still declined: that is a roster read per family, and `/app/me/families`
 * deliberately answers about the caller's own memberships and nothing about anybody else's.
 *
 * **It is shown always rather than only when two names collide**, so nobody learns the fact at
 * the moment it is load-bearing. Two identically-named households are now rare rather than
 * ordinary — #360 refuses a second household under a name this person has already used — but
 * they remain *acquirable*, because the household somebody else names and invites you into is
 * theirs to name.
 */
export function FamilySwitcher({
  families,
  familyId,
}: {
  families: readonly Membership[];
  familyId: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const current = families.find((family) => family.familyId === familyId);

  if (families.length < 2) {
    return (
      <p className="scope">
        <span className="scope__label">Family</span>
        <span className="scope__name">{familyName(current)}</span>
        {standing(current) === null ? null : (
          <span className="scope__standing">{standing(current)}</span>
        )}
      </p>
    );
  }

  return (
    <Menu
      className="scope scope--button"
      label={`Family: ${familyName(current)}. Switch family`}
      trigger={
        <>
          <span className="scope__label">Family</span>
          <span className="scope__name">{familyName(current)}</span>
          {standing(current) === null ? null : (
            <span className="scope__standing">{standing(current)}</span>
          )}
          <ChevronIcon />
        </>
      }
    >
      <MenuLabel>Your families</MenuLabel>
      {families.map((family) => {
        const selected = family.familyId === familyId;
        const note = standing(family);
        return (
          <button
            key={family.familyId}
            className={`menu__item${selected ? " menu__item--current" : ""}`}
            role="menuitemradio"
            aria-checked={selected}
            type="button"
            onClick={() => navigate(swapFamily(location.pathname, family.familyId))}
          >
            <span className="avatar avatar--square" aria-hidden="true" />
            <span className="menu__item-label menu__item-names">
              <span>{familyName(family)}</span>
              {note === null ? null : <span className="menu__item-note">{note}</span>}
            </span>
            {selected ? <CheckIcon /> : null}
          </button>
        );
      })}
      {/*
        The way to a household that is not on this list yet (#360). A link to the page rather
        than a form in a popover: starting a household has refusals worth reading — a name
        already used, the twentieth household — and a menu is the wrong place to read one.
      */}
      <Link className="menu__item" role="menuitem" to={`/me/${HOUSEHOLDS_SECTION.path}`}>
        <span className="menu__item-label">Start another household</span>
      </Link>
    </Menu>
  );
}

/**
 * A family's name for display.
 *
 * The fallback is for a `META` row that carries no name — not something a family can produce
 * (`buildFamilyMetaPut` always writes one), and worth rendering as words rather than as an
 * empty control if it ever happens.
 */
function familyName(family: Membership | undefined): string {
  return family?.name ?? "Your family";
}

/**
 * The caller's own standing in a household, in the panel's words rather than the schema's.
 *
 * `adult` is what the table calls a member who is not an owner, and it is the wrong word on a
 * screen — a member reads "Member", the same way the sidebar reads "Who's here" rather than
 * "the roster" (#220). The roster's own owner pill is the sibling of this and stays as it is:
 * it labels somebody *else*, where this labels you.
 *
 * `null` when the server sent no role — a household being erased, or an older reply. Rendering
 * nothing beats rendering a guess, which is `familyName`'s rule one function up.
 */
export function standing(family: Membership | undefined): string | null {
  if (family === undefined) return null;
  if (family.role === "owner") return "Owner";
  return family.role === "adult" ? "Member" : null;
}

/**
 * Point the current path at a different family, keeping the section the member is on.
 *
 * Switching family while reading "Who's here" should land on "Who's here" — the section is
 * what they are doing and the family is which one they mean. Exported for its own test: this
 * is the one piece of URL arithmetic in the app, and it is what the "the URL is what changed"
 * criterion actually rests on.
 */
export function swapFamily(pathname: string, familyId: string): string {
  const match = /^\/families\/[^/]+(?<tail>\/.*)?$/.exec(pathname);
  return `/families/${familyId}${match?.groups?.tail ?? ""}`;
}
